import { Sparkles } from 'lucide-react';
import { useAssistantUi } from './assistantUiStore';
import { useHasFeature } from '@/lib/permissions/useCompanyFeatures';
import { ATLEDI_ENABLED } from './atlediAvailability';
import { useT } from '@/lib/i18n/useT';

/**
 * Caso 07 — Atledi's compact launcher, rendered globally in `AdminShell`.
 * Toggles the AssistantDrawer.
 *
 * This is now Atledi's ONLY entry point: it used to also occupy the whole
 * right rail, which is reserved for work content. The launcher is the
 * "compact callout" form.
 *
 * The `aiAssistant` plan gate lives here (and in the drawer). It used to sit
 * on the rail's panel only, so moving Atledi out of the rail without this
 * would have handed the paid assistant to every workspace.
 */
export function AssistantLauncher() {
  const t = useT();
  const open = useAssistantUi((s) => s.open);
  const toggle = useAssistantUi((s) => s.toggle);
  const hasFeature = useHasFeature('aiAssistant');
  if (!hasFeature) return null;

  // Shown but inert until Atledi ships — people should know where it will live.
  if (!ATLEDI_ENABLED) {
    return (
      <button
        type="button"
        disabled
        aria-label={`${t('assistant.launcher.aria')} — ${t('assistant.comingSoon')}`}
        title={`${t('assistant.launcher.aria')} — ${t('assistant.comingSoon')}`}
        className="fixed bottom-24 right-6 z-30 inline-flex h-12 w-12 cursor-not-allowed items-center justify-center rounded-full border border-dashed border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] text-[var(--color-fg-subtle)] print:hidden md:bottom-6 md:z-50"
      >
        <Sparkles className="h-5 w-5" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={t('assistant.launcher.aria')}
      aria-pressed={open}
      /* bottom-24 on mobile clears the fixed bottom nav (h-16); md+ has no
         bottom bar so the launcher drops back to the usual bottom-6. */
      className="fixed bottom-24 right-6 z-30 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-cta-primary)] text-[var(--color-fg-on-cta)] shadow-lg transition-transform hover:scale-105 print:hidden md:bottom-6 md:z-50"
    >
      <Sparkles className="h-5 w-5" />
    </button>
  );
}
